/**
 * Opening a saved SSH/SFTP host from the Graph or a Project (owner, 13.09.2026:
 * "Go to" only switched to the Terminal page; it should ask whether to connect
 * over SFTP or open an SSH terminal — and actually connect).
 *
 * Both targets already have store actions that work from any page:
 *   - FTP/SFTP: ftpStore.connect(connId) decrypts the vault item and connects;
 *     the FTP page shows whatever `activeConnId` is.
 *   - Terminal: terminalExtStore.openSshTab(connId, label) adds (or focuses)
 *     the SSH tab; TerminalExtView is always mounted, so the session starts.
 * The connId is the vault item id (graph node `ssh_host:<id>`).
 */
import { invoke } from '@tauri-apps/api/core';
import { setActiveTab } from '../store/uiModalStore';
import { useFtpStore } from '../store/ftpStore';
import { useTerminalExtStore } from '../store/terminalExtStore';
import { VAULT_GATE_UNLOCKED_EVENT } from '../store/vaultStore';

const GATE_WAIT_MS = 5 * 60 * 1000;

/**
 * Resolves true once the vault is unlocked. A Protected vault opens the
 * passphrase gate instead; then this waits for the gate's success event
 * (or gives up after a few minutes, e.g. the user closed the gate).
 */
export async function ensureVaultUnlocked() {
  try {
    const status = await invoke('vault_status');
    if (status?.unlocked) return true;
  } catch {
    return false;
  }
  if (await useFtpStore.getState().unlockVault()) return true;
  return new Promise((resolve) => {
    const done = (ok) => {
      window.removeEventListener(VAULT_GATE_UNLOCKED_EVENT, onUnlocked);
      clearTimeout(timer);
      resolve(ok);
    };
    const onUnlocked = () => done(true);
    const timer = setTimeout(() => done(false), GATE_WAIT_MS);
    window.addEventListener(VAULT_GATE_UNLOCKED_EVENT, onUnlocked);
  });
}

/** Open the FTP/SFTP page on this host and connect to it. */
export async function openHostInSftp(connId) {
  const ftp = useFtpStore.getState();
  ftp.setActiveConnId(connId);
  setActiveTab('ftp.panel');
  if (!(await ensureVaultUnlocked())) return false;
  const state = useFtpStore.getState();
  // Make sure the row exists in the page's list (it is read from the vault).
  if (!state.connections?.some?.((c) => c.id === connId)) await state.loadConnections?.();
  await useFtpStore.getState().connect(connId);
  return true;
}

/** Open (or focus) an SSH terminal tab for this host and show the Terminal page. */
export async function openHostInTerminal(connId, label) {
  setActiveTab('terminal.panel');
  if (!(await ensureVaultUnlocked())) return false;
  useTerminalExtStore.getState().openSshTab(connId, label);
  return true;
}
